import React, {useEffect, useState} from 'react';
import {BrowserRouter as Router, Routes, Route, useNavigate} from 'react-router-dom';
import {
    extractAndSaveAuthData,
    getProductIdFromSessionStorage
} from './utils/tokenUtils';
import { Home } from './pages/Home/Home';
import { ProductDetails } from './pages/ProductDetails/ProductDetails';
import ProductReviews from './pages/ProductReviews/ProductReviews';
import { Favorites } from './pages/Favorites/Favorites';
import { MyPurchases } from './pages/MyPurchases/MyPurchases';
import { MyReviews } from './pages/MyReviews/MyReviews';
import { MyReview } from './pages/MyReviews/MyReview/MyReview';
import { MyPurchase } from './pages/MyPurchases/MyPurchase/MyPurchase';
import { NotFound } from './pages/NotFound/NotFound';
import { Account } from './pages/Account/Account';
import { Layout } from './components/Layout/Layout';
import { LoadingIndicator } from './components/LoadingIndicator/LoadingIndicator';
import { AdminLogin } from './pages/Admin/AdminLogin/AdminLogin';
import { AdminDashboard } from './pages/Admin/AdminDashboard/AdminDashboard';
import { AddProductPage } from './pages/Admin/AddProduct/AddProductPage';
import { UpdateProductPage } from './pages/Admin/UpdateProduct/UpdateProductPage';
import { AdminLayout } from './components/AdminLayout/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import './styles/globals.scss';

function App() {
    const [isAppReady, setIsAppReady] = useState(false);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [shouldRedirectToProduct, setShouldRedirectToProduct] = useState(false);
    const [productId, setProductId] = useState<string | null>(null);

    useEffect(() => {
        const initializeApp = async () => {
            try {
                const {productId: urlProductId} = extractAndSaveAuthData();
                const storedProductId = getProductIdFromSessionStorage();
                if (urlProductId || storedProductId) {
                    setProductId(urlProductId || storedProductId);
                    setShouldRedirectToProduct(true);
                }
            } catch (error) {
                console.error('Ошибка инициализации приложения:', error);
                setLoadingError('Не удалось загрузить приложение');
            } finally {
                setIsAppReady(true);
            }
        };

        initializeApp();
    }, []);

    if (!isAppReady) {
        return <LoadingIndicator message="Инициализация приложения..."/>;
    }

    if (loadingError) {
        return (
            <div className="error-container">
                <h2>Ошибка загрузки</h2>
                <p>{loadingError}</p>
                <button onClick={() => window.location.reload()}>Попробовать снова</button>
            </div>
        );
    }

    return (
        <Router>
            <Layout>
                <Routes>
                    {shouldRedirectToProduct && productId ? (
                        <Route path="/" element={<ProductRedirector productId={productId}/>}/>
                    ) : (
                        <Route path="/" element={<Home/>}/>
                    )}
                    <Route path="/product/:id" element={<ProductDetails/>}/>
                    <Route path="/reviews/:id" element={<ProductReviews/>}/>
                    <Route path="/favorites" element={<Favorites/>}/>
                    <Route path="/my-reviews" element={<MyReviews/>}/>
                    <Route path="/my-review/:id" element={<MyReview/>}/>
                    <Route path="/my-purchases" element={<MyPurchases/>}/>
                    <Route path="/my-purchase/:id" element={<MyPurchase/>}/>
                    <Route path="/account" element={<Account/>}/>
                    <Route path="*" element={<NotFound/>}/>

                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route
                        path="/admin/*"
                        element={
                            <ProtectedRoute>
                                <AdminLayout>
                                    <Routes>
                                        <Route path="dashboard" element={<AdminDashboard />} />
                                        <Route path="product/add" element={<AddProductPage />} />
                                        <Route path="product/update/:id" element={<UpdateProductPage />} />
                                    </Routes>
                                </AdminLayout>
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </Layout>
        </Router>
    );
}

const ProductRedirector: React.FC<{ productId: string }> = ({productId}) => {
    const navigate = useNavigate();
    useEffect(() => {
        navigate(`/product/${productId}`, {replace: true});
        sessionStorage.removeItem('redirectProductId');
    }, [navigate, productId]);

    return <LoadingIndicator message="Перенаправляем на страницу товара..."/>;
};

export default App;